
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { QueryRef } from 'apollo-angular';
import { combineLatest, Observable } from 'rxjs';
import { skip, take, takeUntil } from 'rxjs/operators';

import {
  EntityConnectionTypeDetailsFragment, Exact, GetEntityConnectionsGQL, GetEntityConnectionsQuery
} from 'generated/graphql';

import {
  ENTITY_CONNECTIONS_CHANGED,
  SET_EC_TABLE_SHOW_INACTIVE
} from 'app/_state/entity/view-entity/entity-connections/entity-connection.actions';
import {
  selectEntityConnectionTableShowInactive
} from 'app/_state/entity/view-entity/entity-connections/entity-connection.selectors';
import {
  selectInfoPageFilterText
} from 'app/_state/entity/view-entity/entity-info-page/entity-info-page.selectors';
import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { AddConnectionDialog } from 'entity/dialogs/add-connection/add-connection.dialog';
import { getOppositeEntityTypeFromConnection } from 'entity/helpers/get-connected-to-entity-type';
import { QueryStorageService } from 'query-builder/core/query-storage.service';

import { filterConnectionsOnText } from './entity-connections-filter-on-text';
import { Actions, ofType } from '@ngrx/effects';
import { EntityConnectionSubqueryHelpers } from 'app/query-common/services/EntityConnectionSubqueryHelpers';

@Component({
  selector: 'app-entity-connection-table',
  templateUrl: './entity-connection-table.component.html',
  styleUrls: [`./entity-connection-table.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityConnectionTableComponent extends WithDisposableSubject() implements OnInit, OnChanges {
  @Input() entityId: string;
  @Input() entityTypeId: string;
  @Input() campaignId: string;
  @Input() entityConnectionType: EntityConnectionTypeDetailsFragment;
  /**
   * Used when displayed on tasks page to show Done check mark
   */
  @Input() showTaskDoneMark: boolean;

  private _$includeInactive: Observable<boolean>;

  includeInactiveChecked: boolean;

  connectionsQuery: QueryRef<GetEntityConnectionsQuery, Exact<{
    entityId: string;
    campaignId: string;
    entityConnectionTypeId: string;
    includeInactive?: boolean;
    first?: number;
    after?: string;
  }>>;

  pageSize = 3;
  connectionsResults: any = null;

  totalCount: number;
  pageInfo: { startCursor: string; endCursor: string; hasNextPage: boolean };
  visibleConnections: any[] = null;

  constructor(
    private _dialog: MatDialog,
    private _store: Store,
    private _actions: Actions,
    private _getEntityConnectionsGQL: GetEntityConnectionsGQL,
    private _cd: ChangeDetectorRef,
    private _queryStorageService: QueryStorageService,
    private _router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.initalize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entityId || changes.campaignId || changes.entityConnectionType) {
      if (changes.entityId.isFirstChange()) { return; }
      this.initalize();
    }
  }

  initalize() {
    this._$includeInactive = this._store.select(selectEntityConnectionTableShowInactive({
      campaignId: this.campaignId,
      entityId: this.entityId,
      entityConnectionTypeId: this.entityConnectionType.id
    }));

    // Sync View Model Checkbox;
    this._$includeInactive.pipe(
      takeUntil(this.disposer)
    ).subscribe((includeInactive) => {
      this.includeInactiveChecked = includeInactive;
      this._cd.markForCheck();
    });

    this._$includeInactive.pipe(
      take(1),
    ).subscribe(includeInactive => {
      this.connectionsQuery = this._getEntityConnectionsGQL.watch({
        includeInactive,
        entityId: this.entityId,
        campaignId: this.campaignId,
        entityConnectionTypeId: this.entityConnectionType?.id,
        first: this.pageSize
      });

      this._afterStateLoaded();
    });
  }

  /**
   * Called after async state is loaded via ngOnInit
   */
  private _afterStateLoaded() {
    // TODO: This may need to be disabled if being rendered outside
    // of the info page, to prevent state from leaking.
    const $filterText = this._store.select(
      selectInfoPageFilterText({ entityId: this.entityId, campaignId: this.campaignId })
    ).pipe(
      takeUntil(this.disposer)
    );

    combineLatest([
      this.connectionsQuery.valueChanges,
      $filterText
    ]).pipe(
      takeUntil(this.disposer)
    ).subscribe(([res, filterText]) => {
      const nodes = res.data.getEntityConnections.nodes;
      this.visibleConnections = filterConnectionsOnText(nodes, filterText, this.entityId);
      this.pageInfo = res.data.getEntityConnections.pageInfo;
      this.totalCount = res.data.getEntityConnections.totalCount;
      this._cd.markForCheck();
    });

    // Refetch GraphQL data when includeInactive state variable
    // changes.
    this._$includeInactive.pipe(
      skip(1), // Skip first, to avoid refetching on first load
      takeUntil(this.disposer)
    ).subscribe((includeInactive) => {
      this.connectionsQuery.refetch({
        includeInactive,
        entityId: this.entityId,
        campaignId: this.campaignId,
        entityConnectionTypeId: this.entityConnectionType?.id,
        first: this.pageSize
      });
    });

    this._actions.pipe(ofType(
      ENTITY_CONNECTIONS_CHANGED
    )).pipe(
      takeUntil(this.disposer)
    ).subscribe(_ => {
      this.connectionsQuery.refetch({
        includeInactive: this.includeInactiveChecked,
        entityId: this.entityId,
        campaignId: this.campaignId,
        entityConnectionTypeId: this.entityConnectionType?.id,
        first: this.pageSize
      });
    });
  }

  showInactiveCheckboxChanged(event: MatCheckboxChange) {
    this._store.dispatch(SET_EC_TABLE_SHOW_INACTIVE({
      payload: {
        key: {
          campaignId: this.campaignId,
          entityConnectionTypeId: this.entityConnectionType?.id,
          entityId: this.entityId
        },
        value: {
          includeInactive: event.checked
        }
      }
    }));
  }

  loadMoreClicked() {
    this.fetchMore();
  }

  fetchMore() {
    this.connectionsQuery.fetchMore({
      // query: ... (you can specify a different query. original is used by default)
      variables: {
        after: this.pageInfo.endCursor,
      }
    });
  }

  trackByIdFn(_index: number, item: { id: string }) {
    return item?.id;
  }

  openAddConnectionDialog(entityType?: string) {
    AddConnectionDialog.open(this._dialog, {
      data: {
        entityConnectionType: this.entityConnectionType,
        fromEntityId: this.entityId,
        fromEntityTypeId: this.entityTypeId,
        campaignId: this.campaignId
      }
    });
  }

  /**
   * Returns the EntityType of the entity that the connections is to.
   */
  getConnectedToEntityType() {
    return getOppositeEntityTypeFromConnection(this.entityTypeId, this.entityConnectionType);
  }

  runConnectedToQuery() {
    const qss = this._queryStorageService;
    const entityTypeConnectedId = this.getConnectedToEntityType()?.id;

    const storedQuery = qss.saveQuery(
      this.campaignId,
      EntityConnectionSubqueryHelpers.createConnectedToQuery(this.entityId, entityTypeConnectedId, this.includeInactiveChecked)
    );

    this._router.navigate(['/entity/list'], {
      queryParams: {
        campaignId: this.campaignId,
        clientQueryId: storedQuery.clientQueryId
      }
    });
  }
}
